/**
 * Compose / settings / admin UI strings [WP-F]. Only WP-F edits this file; it is merged into
 * `zh` by zh.ts, so keys stay typed: `t('compose.discardConfirm')`, `t('settings.tabs.general')`,
 * `t('admin.users.create')`.
 *
 * - `compose`: compose windows, recipients, editor, attachments, schedule picker.
 * - `settings`: the settings page tabs.
 * - `admin`: the admin pages.
 * - `composeErrors`: messages for API error codes not already in `zh.errors` (merged into
 *   `zh.errors`; add new codes only, never redefine an existing one).
 *
 * This module must not import zh.ts (zh.ts imports it).
 */
export const compose = {
  newMessage: '新邮件',
  dockLabel: '写邮件窗口',
  windowLabel: '写邮件：{title}',
  minimize: '最小化',
  restore: '展开',
  maximize: '全屏',
  exitMaximize: '退出全屏',
  close: '保存并关闭',
  closing: '正在保存…',
  loading: '正在加载…',
  loadFailed: '无法加载原邮件',
  editorLoadFailed: '无法加载写邮件窗口，请检查网络后重试',
  draftMissing: '此草稿已不存在（可能已发送或已删除）',
  from: '发件人',
  to: '收件人',
  cc: '抄送',
  bcc: '密送',
  addCc: '添加抄送收件人',
  addBcc: '添加密送收件人',
  subject: '主题',
  bodyPlaceholder: '正文',
  bodyLabel: '邮件正文',
  discardConfirm: '舍弃此草稿？',
  recipients: {
    remove: '移除 {name}',
    invalid: '此地址格式无效',
    unknown: '此地址不存在',
    suggestions: '联系人建议',
    kindTeam: '团队',
    kindAlias: '群组',
  },
  saveState: {
    saving: '正在保存…',
    saved: '已保存',
    error: '保存失败',
    conflict: '已在其他窗口修改',
  },
  send: '发送',
  sending: '正在发送…',
  moreSendOptions: '更多发送选项',
  scheduleSend: '定时发送',
  sendDisabledUploading: '请等待附件上传完成',
  formatting: '格式选项',
  attach: '添加附件',
  insertLink: '插入链接',
  insertPhoto: '插入图片',
  signature: '插入签名',
  signatureMenu: {
    insert: '插入签名',
    none: '不使用签名',
    manage: '管理签名',
    empty: '尚未设置签名',
  },
  discard: '舍弃草稿',
  discarded: '草稿已舍弃',
  discardFailed: '无法舍弃草稿：{reason}',
  dropHere: '拖放文件到此处',
  dropHint: '图片将插入正文，其他文件作为附件添加',
  quoted: {
    show: '显示被剪辑的内容',
    hide: '隐藏被剪辑的内容',
    remove: '移除引用的内容',
    frameTitle: '引用的原邮件',
  },
  toolbar: {
    label: '格式',
    undo: '撤销',
    redo: '重做',
    fontSize: '字体大小',
    sizeSmall: '小',
    sizeNormal: '正常',
    sizeLarge: '大',
    sizeHuge: '巨大',
    bold: '粗体',
    italic: '斜体',
    underline: '下划线',
    textColor: '文字颜色',
    defaultColor: '默认颜色',
    colorSwatch: '颜色 {color}',
    align: '对齐',
    alignLeft: '左对齐',
    alignCenter: '居中',
    alignRight: '右对齐',
    numberedList: '编号列表',
    bulletList: '项目符号列表',
    indentLess: '减少缩进',
    indentMore: '增加缩进',
    quote: '引用',
    strike: '删除线',
    clear: '清除格式',
    link: '插入链接',
  },
  link: {
    insertTitle: '插入链接',
    editTitle: '修改链接',
    text: '要显示的文字',
    url: '链接到网址或电子邮件地址',
    urlPlaceholder: 'https://',
    invalidUrl: '请输入有效的网址或电子邮件地址',
    remove: '移除链接',
    apply: '确定',
  },
  attachments: {
    label: '附件',
    unnamed: '未命名文件',
    remove: '移除附件 {name}',
    cancel: '取消上传 {name}',
    uploading: '正在上传 {percent}%',
    failed: '上传失败',
    fileTooLarge: '“{name}”超过了 25 MB 的单个附件大小上限',
    totalTooLarge: '附件总大小不能超过 28 MB，未添加“{name}”',
    emptyFile: '无法添加空文件或文件夹“{name}”',
    uploadFailed: '“{name}”上传失败：{reason}',
    rejectedTitle: '部分文件未添加',
  },
  errors: {
    title: '无法发送邮件',
    noRecipients: '请至少指定一位收件人。',
    invalidAddress: '地址“{address}”无法识别。请确认所有地址的格式都正确无误。',
    tooMany: '“{field}”栏最多只能填写 50 个地址。',
    unknownLocal: '以下收件人地址不存在：{emails}',
    ok: '确定',
  },
  emptySubject: {
    title: '在没有主题的情况下发送此邮件？',
    confirm: '发送',
  },
  conflict: {
    title: '已在其他窗口修改',
    message:
      '此草稿已在其他窗口或设备上修改。你可以重新加载最新版本（此窗口中未保存的更改将会丢失），或用此窗口中的内容覆盖它。',
    reload: '重新加载',
    overwrite: '覆盖',
  },
  closeFailed: {
    title: '无法保存草稿',
    message: '草稿保存失败：{reason}。仍要关闭此窗口吗？未保存的更改将会丢失。',
    confirm: '仍然关闭',
    cancel: '继续编辑',
  },
  toasts: {
    sending: '正在发送…',
    sent: '邮件已发送',
    scheduled: '邮件已定时，将于 {time} 发送',
    undoing: '正在撤销…',
    undone: '已撤销发送',
    view: '查看邮件',
    undo: '撤销',
  },
  schedule: {
    title: '定时发送',
    presetTomorrowMorning: '明天上午 8:00',
    presetTomorrowAfternoon: '明天下午 1:00',
    presetMondayMorning: '下周一上午 8:00',
    custom: '选择日期和时间',
    date: '日期',
    time: '时间',
    timezone: '时区：{tz}（{offset}）',
    tooSoon: '定时时间必须至少在 1 分钟之后',
    tooLate: '定时时间不能晚于 30 天之后',
    invalid: '请选择有效的日期和时间',
    confirm: '定时发送',
    back: '返回',
    current: '当前发送时间：{time}',
  },
  quote: {
    attribution: '在 {date}，{sender} 写道：',
    forwardHeader: '---------- 转发的邮件 ---------',
    from: '发件人：',
    date: '日期：',
    subject: '主题：',
    to: '收件人：',
    cc: '抄送：',
  },
} as const;

export const settings = {
  title: '设置',
  tabsLabel: '设置分类',
  tabs: {
    general: '常规',
    labels: '标签',
    account: '账号',
    templates: '模版与策略',
  },
  notFound: '找不到该设置页面',
  loadFailed: '无法加载设置：{reason}',
  saveBar: {
    save: '保存更改',
    cancel: '取消',
    unsaved: '你有未保存的更改',
  },
  saved: '设置已保存',
  saveFailed: '保存失败：{reason}',
  leave: {
    title: '放弃未保存的更改？',
    message: '你对设置所做的更改尚未保存，离开此页面后这些更改将会丢失。',
    confirm: '放弃更改',
    cancel: '继续编辑',
  },
  general: {
    displayName: '显示名称',
    displayNameHelp: '收件人看到的发件人名称',
    displayNameManaged: '你拥有由管理员管理的地址，显示名称需由管理员修改。',
    undoSend: '撤销发送',
    undoSendHelp: '发送后可以撤销的时间',
    undoSeconds: '{n} 秒',
    undoOff: '关闭',
    pageSize: '每页显示',
    pageSizeOption: '{n} 个会话',
    images: '外部图片',
    imagesAsk: '显示外部图片前询问',
    imagesAlways: '始终显示外部图片',
    timezone: '时区',
    timezoneHelp: '用于显示时间和定时发送',
    signature: '签名',
    signatureHelp: '附加在你撰写的邮件末尾',
    signatureEnabled: '在新邮件和回复中插入此签名',
    signatureEditor: '签名编辑器',
    trusted: '已信任图片发件人',
    trustedHelp: '始终显示来自这些发件人的外部图片',
    trustedEmpty: '暂无已信任的发件人',
    trustedRemove: '移除 {email}',
  },
  validation: {
    displayNameRequired: '请输入显示名称',
    displayNameTooLong: '显示名称不能超过 {max} 个字符',
    signatureTooLong: '签名过长（最多 {max} KB）',
    pageSize: '每页显示数量须在 10 到 100 之间',
    undo: '请选择有效的撤销时间',
    timezone: '请选择有效的时区',
  },
  zones: {
    'Asia/Shanghai': '北京、上海',
    'Asia/Hong_Kong': '香港',
    'Asia/Taipei': '台北',
    'Asia/Singapore': '新加坡',
    'Asia/Tokyo': '东京',
    'Asia/Seoul': '首尔',
    'Asia/Bangkok': '曼谷',
    'Asia/Kolkata': '印度',
    'Asia/Dubai': '迪拜',
    'Europe/Moscow': '莫斯科',
    'Europe/Berlin': '柏林',
    'Europe/Paris': '巴黎',
    'Europe/London': '伦敦',
    UTC: '协调世界时',
    'America/Sao_Paulo': '圣保罗',
    'America/New_York': '纽约',
    'America/Chicago': '芝加哥',
    'America/Denver': '丹佛',
    'America/Los_Angeles': '洛杉矶',
    'Australia/Sydney': '悉尼',
    'Pacific/Auckland': '奥克兰',
  },
  labels: {
    heading: '标签',
    help: '标签可以帮助你整理邮件。删除标签不会删除邮件。',
    create: '新建标签',
    name: '标签名称',
    color: '颜色',
    threads: '会话',
    actions: '操作',
    rename: '修改',
    delete: '删除',
    empty: '还没有标签',
    threadCount: '{count} 个会话',
    createTitle: '新建标签',
    editTitle: '修改标签',
    deleteTitle: '删除标签“{name}”？',
    deleteMessage: '该标签将从所有会话中移除，会话本身不会被删除。',
    nameRequired: '请输入标签名称',
    nameTooLong: '标签名称不能超过 {max} 个字符',
    created: '已创建标签“{name}”',
    updated: '标签已更新',
    deleted: '已删除标签“{name}”',
    colorLabel: '标签颜色',
    chooseColor: '选择颜色 {color}',
    changeColor: '修改“{name}”的颜色',
  },
  account: {
    info: '当前账号',
    name: '名称',
    email: '邮箱',
    role: '角色',
    admin: '管理员',
    member: '成员',
    identities: '可用的发件地址',
    aliasTag: '别名',
    defaultTag: '默认',
    version: '服务器版本',
    storage: '附件存储',
    password: '修改密码',
    passwordHint: '新密码至少需要 8 个字符。修改后，你在其他设备上的登录将会退出。',
    current: '当前密码',
    new: '新密码',
    confirm: '确认新密码',
    submit: '修改密码',
    changed: '密码已修改，其他设备上的登录已退出',
    currentRequired: '请输入当前密码',
    newRequired: '请输入新密码',
    tooShort: '新密码至少需要 {min} 个字符',
    mismatch: '两次输入的新密码不一致',
    sameAsCurrent: '新密码不能与当前密码相同',
    wrongCurrent: '当前密码不正确',
    addressesSection: '邮箱地址',
    addressesHelp: '仅可在管理员开放的域名下创建、修改或删除自己的邮件地址。发往这些地址的邮件会投递到同一收件箱，写信时也可作为发件人。',
    addressManaged: '此域名的地址由管理员管理，不允许修改或删除。',
    addAddress: '添加邮箱地址',
    editAddress: '修改地址',
    setPrimary: '设为默认',
    deleteAddress: '删除地址',
    deleteAddressTitle: '删除邮箱地址 {email}？',
    deleteAddressMessage: '删除后，其他人将无法再向此地址发送邮件。',
    addressAdded: '已添加邮箱地址 {email}',
    addressUpdated: '已更新邮箱地址 {email}',
    addressDeleted: '已删除邮箱地址 {email}',
    primarySet: '已将 {email} 设为默认主邮箱',
    aliasesSection: '我的别名',
    aliasesHelp: '别名由管理员统一创建、分配和管理；个人只能使用已分配的别名。',
    addAlias: '添加别名',
    deleteAlias: '删除别名',
    deleteAliasTitle: '删除别名 {email}？',
    deleteAliasMessage: '删除后，其他人将无法再向此别名发送邮件。',
    aliasAdded: '已添加别名 {email}',
    aliasDeleted: '已删除别名 {email}',
    noAvailableDomainsForAddress: '当前没有对用户开放添加/修改地址的域名',
    noAvailableDomainsForAlias: '当前没有对用户开放自建别名的域名',
    localPart: '前缀',
    selectDomain: '选择域名',
    displayNameOptional: '显示名称（可选）',
  },
  // [MP-D] Settings → 账号 → 应用专用密码 (DESIGN Addendum C.10)
  appPasswords: {
    heading: '应用专用密码',
    help: '邮件客户端（如 iPhone 邮件、Outlook、Thunderbird）不能使用网页登录密码，需为每台设备创建应用专用密码。修改登录密码会撤销全部应用专用密码。',
    create: '创建应用专用密码',
    empty: '还没有应用专用密码',
    name: '设备名称',
    created: '创建时间',
    lastUsed: '上次使用',
    actions: '操作',
    neverUsed: '从未使用',
    hintLabel: '密码末 4 位',
    revoke: '撤销',
    revokeNamed: '撤销 {name}',
    revokeTitle: '撤销应用专用密码“{name}”？',
    revokeMessage: '撤销后使用该密码的设备将无法再收发邮件。',
    revoked: '已撤销应用专用密码“{name}”',
    createTitle: '创建应用专用密码',
    createHelp: '为使用此密码的设备或应用起个名字，方便以后识别。',
    namePlaceholder: '例如：iPhone 邮件',
    generate: '生成',
    nameRequired: '请输入设备名称',
    nameTooLong: '设备名称不能超过 {max} 个字符',
    nameControl: '设备名称不能包含控制字符',
    nameInvalid: '设备名称无效（1–{max} 个字符，不能包含控制字符）',
    tooMany: '最多只能创建 {limit} 个应用专用密码，请先撤销不用的密码',
    currentPassword: '当前登录密码',
    currentPasswordHelp: '为保护账号安全，请输入当前登录密码',
    currentPasswordRequired: '请输入当前登录密码',
    wrongPassword: '当前密码不正确',
    tooManyAttempts: '尝试次数过多，请稍后再试',
    retryIn: '尝试次数过多，请在 {time}后重试',
    seconds: '{n} 秒',
    minutes: '{n} 分钟',
    createdTitle: '应用专用密码已生成',
    createdFor: '在“{name}”的邮件客户端中，用下面的密码代替你的登录密码：',
    passwordLabel: '应用专用密码',
    spacesHint: '输入时空格可以省略，大小写均可。',
    copyPassword: '复制密码',
    copied: '已复制',
    copyFailed: '无法复制，请手动选择密码后复制',
    showOnce: '此密码只显示一次，关闭后无法再次查看。',
    done: '完成',
    protoSmtp: 'SMTP',
    protoImap: 'IMAP',
    protoPop3: 'POP3',
  },
  // [MP-D] Settings → 账号 → 在邮件客户端中使用 (DESIGN Addendum C.10)
  mailClients: {
    heading: '在邮件客户端中使用',
    help: '通过 IMAP、POP3 和 SMTP，在手机或电脑上的邮件客户端中收发此邮箱的邮件。',
    disabled: '管理员尚未开启邮件客户端访问',
    server: '服务器',
    username: '用户名',
    password: '密码',
    passwordValue: '应用专用密码（不是登录密码）',
    gotoAppPasswords: '创建应用专用密码',
    protocol: '协议',
    port: '端口',
    security: '加密方式',
    auth: '身份验证',
    imap: 'IMAP 收信',
    pop3: 'POP3 收信',
    smtp: 'SMTP 发信',
    recommended: '推荐',
    tls: 'SSL/TLS',
    starttls: 'STARTTLS',
    authRequired: '需要身份验证',
    authSame: '与收信相同的用户名和密码',
    copy: '复制{what}',
    copyPort: '复制 {name} 端口',
    copied: '已复制',
    copyFailed: '无法复制，请手动选择后复制',
    notesHeading: '使用须知',
    noteImap: '推荐使用 IMAP：所有文件夹、标签、已读和星标状态会在网页版和各设备之间同步。',
    notePop3: 'POP3 只下载收件箱；在客户端中删除邮件会将其移到“已删除”（30 天内可在网页版恢复）；POP3 不会把邮件标为已读。',
    noteSent: '已发送邮件会自动保存到“已发送”，请在客户端关闭“在服务器上保存已发送邮件”以免重复。',
    noteSmtp: '通过客户端发送的邮件没有撤销发送时间；如果发送失败，会在网页版的“已发送”中显示“发送失败”。',
    noSmtp: '管理员未开启 SMTP 发信，客户端只能收信。',
    noIncoming: '管理员未开启 IMAP 和 POP3，客户端只能发信。',
    guidesHeading: '设置步骤',
    guidesLabel: '选择邮件客户端',
    portsIn: '收件 {port}（{security}）',
    portsOut: '发件 {port}（STARTTLS）',
    clients: {
      ios: 'iPhone / iPad',
      macos: 'Mac 邮件',
      outlook: 'Outlook',
      thunderbird: 'Thunderbird',
      android: 'Android',
    },
    guides: {
      ios: {
        open: '打开“设置”→“邮件”（iOS 18 起为“设置”→“App”→“邮件”）→“邮件账户”→“添加账户”→“其他”→“添加邮件账户”。',
        account: '填写姓名、电子邮件 {user}，密码填写应用专用密码，点“下一步”。',
        incoming: '选择“{inProto}”，在“收件服务器”中填写主机名 {host}、用户名 {user} 和应用专用密码。',
        outgoing: '在“发件服务器”中填写主机名 {host}、用户名 {user} 和同一个应用专用密码（发件服务器的用户名和密码不能留空）。',
        finish: '点“下一步”并存储。如果连接失败，在该账户的“高级”设置中确认：{ports}，并打开“使用 SSL”。',
      },
      macos: {
        open: '打开“邮件”，选择菜单“邮件”→“添加账户…”→“其他邮件账户…”，点“继续”。',
        account: '填写名称、电子邮件地址 {user}，密码填写应用专用密码，点“登录”。',
        incoming: '提示无法验证时，账户类型选择“{inProto}”，收件服务器填写 {host}，用户名 {user}。',
        outgoing: '发件服务器填写 {host}，用户名和密码与收件服务器相同。',
        finish: '如需手动设置端口：“邮件”→“设置”→“账户”→“服务器设置”，取消勾选“自动管理连接设置”，填写 {ports}。',
      },
      outlook: {
        open: '选择“文件”→“添加账户”（新版 Outlook：“设置”→“账户”→“添加账户”），输入 {user}。',
        account: '选择“高级选项”→“让我手动设置我的账户”。',
        incoming: '账户类型选择“{inProto}”。接收邮件：服务器 {host}，端口 {inPort}，加密方法 SSL/TLS。',
        outgoing: '发送邮件：服务器 {host}，端口 {smtpPort}，加密方法 STARTTLS，勾选“需要身份验证”（与接收邮件使用相同的用户名和密码）。',
        finish: '密码填写应用专用密码，点“连接”。',
      },
      thunderbird: {
        open: '打开 Thunderbird，选择“新建”→“已有邮件账户…”，填写姓名、{user} 和应用专用密码。',
        account: '点“手动配置”。',
        incoming: '收件服务器：协议 {inProto}，主机名 {host}，端口 {inPort}，连接安全 SSL/TLS，验证方式“普通密码”，用户名 {user}。',
        outgoing: '发件服务器（SMTP）：主机名 {host}，端口 {smtpPort}，连接安全 STARTTLS，验证方式“普通密码”，用户名 {user}。',
        finish: '点“完成”。',
      },
      android: {
        open: 'Gmail 应用：点右上角头像 →“添加其他账号”→“其他”；系统邮件应用：“添加账户”→“其他邮箱”。',
        account: '输入 {user}，选择“手动设置”，密码填写应用专用密码。',
        incoming: '账号类型选择“{inProto}”。收件服务器 {host}，端口 {inPort}，安全类型 SSL/TLS。',
        outgoing: '外发服务器（SMTP）{host}，端口 {smtpPort}，安全类型 STARTTLS，保持“需要登录”开启。',
        finish: '完成后即可在应用中收发邮件。',
      },
    },
  },
} as const;

export const admin = {
  title: '管理后台',
  tabsLabel: '管理分类',
  forbidden: '你没有访问管理后台的权限',
  backToMail: '返回邮件',
  tabs: {
    users: '用户',
    aliases: '别名',
    domains: '域名',
    events: 'Webhook 事件',
    outbox: '发件队列',
    stats: '统计',
  },
  common: {
    loadFailed: '加载失败：{reason}',
    retry: '重试',
    refresh: '刷新',
    yes: '是',
    no: '否',
    never: '从未',
    none: '—',
    actions: '操作',
    edit: '修改',
    delete: '删除',
    copy: '复制',
    prev: '上一页',
    next: '下一页',
    page: '第 {n} 页',
    all: '全部',
    saveFailed: '保存失败：{reason}',
    deleteFailed: '删除失败：{reason}',
    showPassword: '显示密码',
    hidePassword: '隐藏密码',
  },
  validation: {
    localRequired: '请输入邮箱地址',
    localInvalid: '只能包含字母、数字和 . _ + -，且不能以 . 开头或结尾',
    localTooLong: '邮箱地址不能超过 64 个字符',
    domainRequired: '请选择域名',
    domainInvalid: '请输入有效的域名，例如 example.com',
    nameRequired: '请输入显示名称',
    nameTooLong: '显示名称不能超过 {max} 个字符',
    passwordRequired: '请输入初始密码',
    passwordTooShort: '密码至少需要 {min} 个字符',
  },
  users: {
    create: '创建用户',
    name: '名称',
    email: '邮箱',
    admin: '管理员',
    status: '状态',
    lastLogin: '最近登录',
    messages: '邮件数',
    storage: '存储',
    active: '正常',
    disabled: '已停用',
    you: '你',
    empty: '还没有用户',
    createTitle: '创建用户',
    editTitle: '修改用户 {email}',
    address: '邮箱地址',
    localPlaceholder: '例如 zhangsan',
    domain: '域名',
    displayName: '显示名称',
    password: '初始密码',
    passwordHelp: '至少 8 个字符，用户登录后可以自行修改',
    isAdmin: '管理员',
    isAdminHelp: '可以访问管理后台',
    disabledLabel: '停用账号',
    disabledHelp: '停用后该用户将无法登录，现有登录会立即退出',
    resetPassword: '重置密码',
    resetPasswordHelp: '留空则不修改；修改后该用户的所有登录都会退出',
    noDomains: '请先在“域名”中添加域名',
    deleteTitle: '删除用户 {email}？',
    deleteMessage: '该用户的所有邮件、草稿和设置将被永久删除，且无法恢复。',
    created: '已创建用户 {email}',
    updated: '已更新用户 {email}',
    deleted: '已删除用户 {email}',
    cannotDeleteSelf: '不能删除自己的账号',
    cannotDisableSelf: '不能停用自己的账号',
    aliasesTitle: '所属别名',
  },
  aliases: {
    create: '创建别名',
    email: '别名地址',
    displayName: '显示名称',
    members: '成员',
    shareSent: '共享已发送',
    shareSentHelp: '成员以此别名发出的邮件会出现在其他成员的会话中',
    canSendAs: '可代发',
    canSendAsHelp: '允许以此别名发送邮件',
    noMembers: '没有成员',
    noMembersHint: '没有成员时，发送到此别名的邮件将无法投递',
    pickMembers: '选择成员',
    searchUsers: '搜索用户',
    noUsers: '没有可添加的用户',
    removeMember: '移除 {name}',
    createTitle: '创建别名',
    editTitle: '修改别名 {email}',
    deleteTitle: '删除别名 {email}？',
    deleteMessage: '删除后，发送到此地址的邮件将无法投递。',
    created: '已创建别名 {email}',
    updated: '已更新别名 {email}',
    deleted: '已删除别名 {email}',
    empty: '还没有别名',
    memberCount: '{count} 位成员',
  },
  domains: {
    add: '添加域名',
    name: '域名',
    namePlaceholder: 'example.com',
    receiving: '收件',
    createdAt: '添加时间',
    status: '查看 DNS 状态',
    deleteTitle: '删除域名 {name}？',
    deleteMessage: '只有没有邮箱和别名使用时才能删除域名。',
    added: '已添加域名 {name}',
    deleted: '已删除域名 {name}',
    updated: '已更新域名 {name} 配置',
    empty: '还没有域名',
    allowUserAddress: '开放个人地址管理',
    allowUserAddressHelp: '允许普通用户创建、改名和删除自己在此域名下的邮件地址；关闭后已有地址也不可修改或删除。别名始终由管理员管理。',
    statusTitle: '{name} 的 DNS 状态',
    resendStatus: 'Resend 验证状态',
    region: '区域',
    notInResend: 'Resend 中找不到此域名。请先在 Resend 控制台添加该域名，然后按提示配置下列 DNS 记录。',
    records: 'DNS 记录',
    recordType: '类型',
    recordName: '名称',
    recordValue: '值',
    recordPriority: '优先级',
    recordTtl: 'TTL',
    recordStatus: '状态',
    copyValue: '复制值',
    noRecords: '没有 DNS 记录',
    states: {
      not_started: '未开始',
      pending: '验证中',
      verified: '已验证',
      failed: '验证失败',
      temporary_failure: '暂时失败',
    },
  },
  events: {
    type: '类型',
    allTypes: '全部类型',
    received: '接收时间',
    email: '邮件 ID',
    svix: 'Svix ID',
    processed: '处理时间',
    result: '结果',
    empty: '没有 Webhook 事件',
    results: {
      applied: '已应用',
      enqueued: '已入队',
      ignored_unknown: '已忽略（未知邮件）',
      duplicate: '重复事件',
      error: '错误',
      pending: '未处理',
    },
  },
  outbox: {
    sectionsLabel: '队列类型',
    sections: {
      outbound: '发件',
      inbound: '收件异常',
      jobs: '失败任务',
    },
    filters: {
      failed: '失败',
      queued: '排队中',
      sending: '发送中',
      unroutable: '无法投递',
      inboundFailed: '处理失败',
    },
    id: '编号',
    sender: '发送者',
    from: '发件地址',
    status: '状态',
    detail: '详情',
    scheduled: '定时',
    size: '大小',
    updated: '更新时间',
    retry: '重试',
    retried: '已重新加入发送队列（新编号 #{id}）',
    empty: '没有符合条件的发件记录',
    via: {
      resend: 'Resend 定时',
      local: '本地定时',
    },
    inbound: {
      from: '发件人',
      subject: '主题',
      recipients: '收件地址',
      state: '状态',
      error: '错误',
      received: '接收时间',
      source: '来源',
      empty: '没有收件异常',
      states: {
        pending: '待处理',
        delivered: '已投递',
        unroutable: '无法投递',
        failed: '处理失败',
      },
      sources: {
        webhook: 'Webhook',
        poll: '轮询',
        admin: '手动同步',
      },
    },
    jobs: {
      kind: '任务类型',
      lane: '队列',
      attempts: '尝试次数',
      error: '最后错误',
      runAt: '计划时间',
      empty: '没有失败的任务',
      retried: '任务 #{id} 已重新排队',
    },
  },
  stats: {
    users: '用户',
    messages: '邮件',
    mailboxBytes: '邮箱占用 {size}',
    storage: '附件存储',
    blobs: '{count} 个文件 · {size}',
    queue: '任务队列',
    pending: '待处理',
    pendingPeriodic: '（其中周期任务 {count}）',
    dead: '失败',
    pollGapTitle: '检测到收件轮询存在缺口',
    pollGapHint: '检测时间 {time}。部分邮件未通过 Webhook 送达（也可能已丢失），请检查 Webhook 配置。',
    last24h: '最近 24 小时',
    sent: '发送',
    received: '接收',
    failed: '失败',
    syncStatus: '同步状态',
    lastWebhook: '最近一次 Webhook',
    lastPoll: '最近一次轮询',
    sync: '立即同步收件',
    syncStarted: '已开始同步收件（任务 #{id}）',
    quotaBlocked: 'Resend 发送配额已用完，发信已暂停。配额恢复后，可以在“发件队列”中重试失败的邮件。',
    backends: {
      local: '本地磁盘',
      r2: 'Cloudflare R2',
    },
    deliveries: {
      proxy: '经服务器代理',
      redirect: '直接重定向',
    },
    updatedAt: '更新于 {time}',
  },
} as const;

/** Messages for error codes not in zh.ts `errors` (shown when the server sends no message). */
export const composeErrors = {
  weak_password: '密码强度不足，请换一个更长或更复杂的密码',
  last_admin: '至少需要保留一位管理员',
  cannot_delete_self: '不能删除自己的账号',
  alias_in_use: '此别名已用于发送邮件，无法删除。可以移除它的所有成员来停用它。',
  domain_in_use: '仍有邮箱或别名在使用此域名，无法删除',
  domain_exists: '该域名已存在',
  address_exists: '该邮箱地址已被使用',
  unknown_domain: '域名不存在，请先添加域名',
  no_recipients: '请至少指定一位收件人',
  invalid_state: '当前状态下无法执行此操作',
  storage_error: '文件存储出错，请联系管理员',
  storage_unavailable: '文件存储暂时不可用，请稍后重试',
  alias_admin_only: '别名仅可由管理员创建、删除和管理',
  domain_user_alias_disabled: '别名仅可由管理员创建、删除和管理',
  domain_user_address_disabled: '该域名未开放个人地址管理，不能创建、修改或删除地址',
  cannot_delete_primary_address: '不能删除默认主邮箱地址，请先将其他地址设为默认',
  address_in_use: '该地址已有发信记录，无法直接删除',
  cannot_delete_shared_alias: '该别名包含其他成员，无法直接删除',
} as const;
